import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

/**
 * If your app uses a real AuthContext hook, you can swap this.
 * For now: keep compatibility with your current "kc_token" / "kc_user" localStorage logic.
 */
function useAuthCompat() {
  try {
    const raw = localStorage.getItem("kc_token");
    const token = raw || "";
    const user = JSON.parse(localStorage.getItem("kc_user") || "{}");
    return { token, user };
  } catch {
    return { token: "", user: {} };
  }
}

function safeJsonParse(s, fallback) {
  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
}

function guessKindFromUrl(url = "") {
  const u = String(url || "").toLowerCase();
  if (u.match(/\.(png|jpe?g|webp|gif)(\?|#|$)/)) return "image";
  if (u.match(/\.(mp4|mov|m4v|webm|mkv|avi)(\?|#|$)/)) return "video";
  return "file";
}

function guessKindFromFile(file) {
  const t = (file?.type || "").toLowerCase();
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("video/")) return "video";
  const n = (file?.name || "").toLowerCase();
  return guessKindFromUrl(n);
}

function extractFirstUrlFromText(text = "") {
  const s = String(text || "");
  // Prefer /uploads/... or https://... (keep it simple & robust)
  const m =
    s.match(/(https?:\/\/[^\s]+|\/uploads\/[^\s]+)/i) ||
    s.match(/(uploads\/[^\s]+)/i);
  return m ? m[1] : "";
}

function normalizeMaybeUploadsUrl(raw = "") {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) return s;
  if (s.startsWith("uploads/")) return `/${s}`;
  return `/uploads/${s}`;
}

export default function ProjectForum() {
  const { id } = useParams();
  const { token, user } = useAuthCompat();

  // Project reminder
  const [project, setProject] = useState(null);
  const [loadingProject, setLoadingProject] = useState(true);

  // Messages
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState("");
  const [usersByUsername, setUsersByUsername] = useState({});

  // Avoid calling a non-existing endpoint on every upload (404 spam)
  // null = unknown, false = definitely not available, true = available
  const [directUploadAvailable, setDirectUploadAvailable] = useState(null);

  const chatListRef = useRef(null);

  const headersJson = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  const authOnlyHeaders = useMemo(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  function resolveUsername() {
    // Prefer local kc_user, else fallback to the passed user object
    const u = safeJsonParse(localStorage.getItem("kc_user") || "{}", {});
    return (
      u.preferred_username ||
      u.username ||
      (u.email ? u.email.split("@")[0] : "") ||
      user?.preferred_username ||
      user?.username ||
      (user?.email ? String(user.email).split("@")[0] : "") ||
      "anonymous"
    );
  }

  // Load users (to show real avatar photo if available)
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch("/users", { headers: authOnlyHeaders });
        if (!r.ok) return;
        const arr = await r.json();
        const map = {};
        (Array.isArray(arr) ? arr : []).forEach((u) => {
          const key = (u?.username || u?.user || u?.login || u?.email || "").trim();
          if (key) map[key] = u;
        });
        setUsersByUsername(map);
      } catch {
        /* ignore */
      }
    })();
  }, [token, authOnlyHeaders]);

  function avatarFor(username) {
    const u = usersByUsername[username] || {};
    const candidate =
      u.avatar_url ||
      u.avatar ||
      u.photo_url ||
      u.picture ||
      u.profile_photo ||
      u.profile_picture ||
      u.profilePicture ||
      u.image_url ||
      "";

    const normalized = normalizeMaybeUploadsUrl(candidate);
    return (
      normalized ||
      `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(username || "user")}`
    );
  }

  // Load project + messages + poll
  useEffect(() => {
    let alive = true;

    async function loadProject() {
      try {
        setLoadingProject(true);
        const r = await fetch(`/projects/${id}`, { headers: headersJson });
        if (r.ok) {
          const p = await r.json();
          if (alive) setProject(p);
        }
      } catch {
        /* ignore */
      } finally {
        if (alive) setLoadingProject(false);
      }
    }

    async function loadMessages() {
      try {
        setLoadingMsgs(true);
        const r = await fetch(`/projects/${id}/messages`, {
          headers: authOnlyHeaders,
        });
        if (r.ok) {
          const arr = await r.json();
          if (alive) setMessages(Array.isArray(arr) ? arr : []);
        }
      } catch {
        /* ignore */
      } finally {
        if (alive) setLoadingMsgs(false);
      }
    }

    loadProject();
    loadMessages();

    const poll = setInterval(loadMessages, 15000);
    return () => {
      alive = false;
      clearInterval(poll);
    };
  }, [id, headersJson, authOnlyHeaders]);

  // Autoscroll
  useEffect(() => {
    if (chatListRef.current) {
      chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
    }
  }, [messages]);

  // Attachment preview URL lifecycle
  useEffect(() => {
    if (!attachment) {
      if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
      setAttachmentPreview("");
      return;
    }
    const url = URL.createObjectURL(attachment);
    setAttachmentPreview(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [attachment]);

  async function reloadMessages() {
    try {
      const r = await fetch(`/projects/${id}/messages`, { headers: authOnlyHeaders });
      if (r.ok) {
        const arr = await r.json();
        setMessages(Array.isArray(arr) ? arr : []);
      }
    } catch {
      /* ignore */
    }
  }

  async function uploadViaMediaEndpoint(file, messageText, username) {
    // Uses your existing /upload endpoint (already used in MyMedia.js)
    const fd = new FormData();
    fd.append("media", file);
    fd.append("title", (messageText || file?.name || "Pièce jointe").trim().slice(0, 140));
    fd.append("location", "Forum");
    fd.append("visibility", "PRIVATE");

    const res = await fetch("/upload", {
      method: "POST",
      headers: authOnlyHeaders,
      body: fd,
    });

    if (!res.ok) {
      const err = await res.text().catch(() => "");
      throw new Error(err || "Upload failed");
    }
    const result = await res.json().catch(() => ({}));
    const fileUrl =
      result?.file_url ||
      result?.url ||
      (result?.file_path ? normalizeMaybeUploadsUrl(result.file_path) : "") ||
      "";
    return { result, fileUrl };
  }

  async function sendMessage() {
    const trimmed = (newMessage || "").trim();
    if (!trimmed && !attachment) return;

    const username = resolveUsername();
    setSending(true);

    try {
      // 1) If there is an attachment, try the direct forum upload route (if it exists),
      //    otherwise fallback to /upload + classic /projects/:id/messages.
      if (attachment) {
        let directWorked = false;

        if (directUploadAvailable !== false) {
          try {
            const form = new FormData();
            form.append("text", trimmed || "");
            form.append("file", attachment);
            form.append("user_id", username);

            const r = await fetch(`/projects/${id}/messages/upload`, {
              method: "POST",
              headers: authOnlyHeaders,
              body: form,
            });

            if (r.ok) {
              const saved = await r.json().catch(() => null);
              setDirectUploadAvailable(true);
              setNewMessage("");
              setAttachment(null);
              if (saved) setMessages((prev) => [...prev, saved]);
              else await reloadMessages();
              directWorked = true;
            } else if (r.status === 404) {
              setDirectUploadAvailable(false);
            }
          } catch {
            // keep fallback
          }
        }

        if (directWorked) return;

        // Fallback path: /upload (media) then /projects/:id/messages (text + url)
        const { fileUrl } = await uploadViaMediaEndpoint(attachment, trimmed, username);

        const payload = {
          user_id: username,
          message: trimmed ? `${trimmed}\n${fileUrl}` : fileUrl,
          file_url: fileUrl,
          file_name: attachment?.name || "",
          file_type: attachment?.type || "",
        };

        const r2 = await fetch(`/projects/${id}/messages`, {
          method: "POST",
          headers: headersJson,
          body: JSON.stringify(payload),
        });

        if (r2.ok) {
          setNewMessage("");
          setAttachment(null);
          await reloadMessages();
        }
        return;
      }

      // 2) Text-only message (classic route)
      const r = await fetch(`/projects/${id}/messages`, {
        method: "POST",
        headers: headersJson,
        body: JSON.stringify({ user_id: username, message: trimmed }),
      });

      if (r.ok) {
        setNewMessage("");
        await reloadMessages();
      }
    } catch {
      // Optionally toast here
    } finally {
      setSending(false);
    }
  }

  function handlePickFile(e) {
    const file = e.target.files?.[0] || null;
    if (file) setAttachment(file);
    // allow re-picking the same file
    if (e.target) e.target.value = "";
  }

  function handleComposerKeyDown(e) {
    // Enter => send; Shift+Enter => newline
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      if (!sending) sendMessage();
    }
  }

  const composerKind = attachment ? guessKindFromFile(attachment) : "file";

  const styles = `
    .forum-scroll::-webkit-scrollbar { width: 10px; }
    .forum-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,.12); border-radius: 10px; }
    .forum-scroll { scrollbar-width: thin; }

    .forum-attach-preview {
      max-width: 340px;
      border-radius: 14px;
      overflow: hidden;
      border: 1px solid rgba(0,0,0,.08);
      background: #fff;
    }
    .forum-attach-preview img,
    .forum-attach-preview video {
      width: 100%;
      height: auto;
      display: block;
      object-fit: contain;
      background: #111;
    }

    .forum-bubble {
      background: #fff;
      border: 1px solid rgba(0,0,0,.06);
      border-radius: 14px;
      padding: 10px 12px;
      box-shadow: 0 4px 16px rgba(0,0,0,.06);
    }
  `;

  return (
    <div className="container py-3">
      <style>{styles}</style>

      {/* Header / Breadcrumbs */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <Link className="btn btn-light border" to="/dashboard">
            <i className="bi bi-grid" /> Dashboard
          </Link>
          <span className="text-muted">/</span>
          <span className="fw-semibold">Forum du projet</span>
        </div>

        <div className="d-flex gap-2">
          <a className="btn btn-outline-secondary" href={`https://weavevo.com/project/${id}/manage`}>
            <i className="bi bi-scissors" /> Revenir au studio
          </a>
          <button className="btn btn-outline-primary" onClick={() => window.location.reload()} type="button">
            <i className="bi bi-arrow-clockwise" /> Actualiser
          </button>
        </div>
      </div>

      {/* Project reminder card */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body">
          <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
            <div className="flex-grow-1">
              <h5 className="mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-info-circle" />
                Détails du projet
              </h5>
              {loadingProject ? (
                <div className="text-muted small">Chargement…</div>
              ) : (
                <>
                  <div className="mb-1">
                    <span className="text-muted small">ID :</span> <code>{id}</code>
                  </div>
                  <div className="mb-0">
                    <div className="text-muted small mb-1">Description</div>
                    <div className="p-2 rounded bg-light border">{project?.description || "—"}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Forum */}
      <div className="row g-3">
        <div className="col-12 col-lg-8">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-0 d-flex flex-column" style={{ height: 520 }}>
              <div
                ref={chatListRef}
                className="flex-grow-1 p-3 overflow-auto forum-scroll"
                style={{ background: "linear-gradient(180deg,#f8fbff,#ffffff)" }}
              >
                {loadingMsgs && <div className="text-muted small">Chargement des messages…</div>}
                {!loadingMsgs && messages.length === 0 && (
                  <div className="text-muted small">Aucun message pour le moment.</div>
                )}

                {messages.map((m) => {
                  const username = m.user_id || m.author || "??";
                  const content = (m.message ?? m.text ?? "").toString();
                  const when = m.created_at
                    ? new Date(m.created_at)
                    : m.createdAt
                      ? new Date(m.createdAt)
                      : null;
                  const whenTxt = when ? when.toLocaleString() : "";

                  const explicitUrl =
                    m.file_url || m.attachment_url || m.url || m.media_url || "";
                  const urlFromText = extractFirstUrlFromText(content);
                  const mediaUrlRaw = explicitUrl || urlFromText || "";
                  const mediaUrl = mediaUrlRaw ? normalizeMaybeUploadsUrl(mediaUrlRaw) : "";
                  const kind = mediaUrl ? guessKindFromUrl(mediaUrl) : "";

                  const safeAvatar = avatarFor(username);

                  return (
                    <div key={m.id || `${username}-${Math.random()}`} className="d-flex gap-2 mb-3">
                      <img
                        src={safeAvatar}
                        alt={username}
                        width={36}
                        height={36}
                        className="rounded-circle border"
                        onError={(e) => {
                          e.currentTarget.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
                            username || "user"
                          )}`;
                        }}
                      />
                      <div className="flex-grow-1">
                        <div className="forum-bubble">
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2">
                              <strong>@{username}</strong>
                              <span className="text-muted small">{whenTxt}</span>
                            </div>
                          </div>

                          {content ? (
                            <p className="mb-2" style={{ whiteSpace: "pre-wrap" }}>
                              {content}
                            </p>
                          ) : null}

                          {mediaUrl ? (
                            <div className="mt-1">
                              <div className="forum-attach-preview">
                                {kind === "image" ? (
                                  <img src={mediaUrl} alt="attachment" loading="lazy" decoding="async" />
                                ) : kind === "video" ? (
                                  <video src={mediaUrl} controls preload="metadata" playsInline />
                                ) : (
                                  <div className="p-2 small">
                                    <i className="bi bi-paperclip me-1" />
                                    <a href={mediaUrl} target="_blank" rel="noreferrer">
                                      Ouvrir la pièce jointe
                                    </a>
                                  </div>
                                )}
                              </div>

                              <div className="small text-muted mt-2">
                                <i className="bi bi-box-arrow-up-right me-1" />
                                <a href={mediaUrl} target="_blank" rel="noreferrer">
                                  Ouvrir en plein écran
                                </a>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Composer */}
              <div className="border-top p-3">
                <div className="d-flex align-items-start gap-2">
                  <button
                    className="btn btn-light border"
                    type="button"
                    onClick={() => document.getElementById("forumFileInput")?.click()}
                    title="Ajouter une photo / vidéo"
                  >
                    <i className="bi bi-plus-lg" />
                  </button>

                  <input
                    id="forumFileInput"
                    type="file"
                    className="d-none"
                    accept="image/*,video/*"
                    onChange={handlePickFile}
                  />

                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="Écrire un message… (Entrée = envoyer / Shift+Entrée = retour ligne)"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleComposerKeyDown}
                  />

                  <button className="btn btn-primary" type="button" disabled={sending} onClick={sendMessage}>
                    {sending ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" /> Envoi…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-1" /> Envoyer
                      </>
                    )}
                  </button>
                </div>

                {/* Attachment preview */}
                {attachment ? (
                  <div className="mt-2 d-flex align-items-start justify-content-between gap-2 flex-wrap">
                    <div className="small text-muted d-flex align-items-center gap-2">
                      <i className={composerKind === "video" ? "bi bi-camera-video" : "bi bi-image"} />
                      <span>
                        Fichier : <strong>{attachment.name}</strong>
                      </span>
                    </div>

                    <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setAttachment(null)}>
                      Retirer
                    </button>

                    {attachmentPreview ? (
                      <div className="forum-attach-preview mt-2">
                        {composerKind === "image" ? (
                          <img src={attachmentPreview} alt="preview" />
                        ) : composerKind === "video" ? (
                          <video src={attachmentPreview} controls preload="metadata" playsInline />
                        ) : (
                          <div className="p-2 small">
                            <i className="bi bi-paperclip me-1" />
                            Aperçu indisponible
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* Right panel (context/help) */}
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <h6 className="mb-3">Raccourcis</h6>
              <div className="d-grid gap-2">
                <a className="btn btn-outline-secondary" href={`https://weavevo.com/project/${id}/manage`}>
                  <i className="bi bi-scissors" /> Revenir au studio (montage)
                </a>
              </div>

              <hr />

              <div className="small text-muted">
                Utilisez ce forum pour coordonner l’équipe autour du projet. Le bouton <i className="bi bi-plus-lg" /> permet
                d’ajouter une photo ou une vidéo (upload via <code>/upload</code> si <code>/messages/upload</code> n’existe pas).
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
